import { supabase } from '../lib/supabase';
import { logSupabaseError } from './errorLogService';


/**
 * Monitoring Service
 * Provides real-time metrics aggregation, error trends, database performance, and email delivery health
 */

/**
 * Fetch real-time application status statistics
 * @returns {Object} Application status metrics
 */
export const getApplicationStatusMetrics = async () => {
  try {
    const { data: applications, error } = await supabase
      ?.from('applications')
      ?.select('application_status, created_at');

    if (error) {
      await logSupabaseError({
        error,
        query: 'getApplicationStatusMetrics',
        table: 'applications',
        operation: 'SELECT',
        componentName: 'monitoringService',
        functionName: 'getApplicationStatusMetrics'
      });
      throw error;
    }

    const total = applications?.length || 0;
    const pending = applications?.filter(app => app?.application_status === 'pending')?.length || 0;
    const processing = applications?.filter(app => app?.application_status === 'processing')?.length || 0;
    const approved = applications?.filter(app => app?.application_status === 'approved')?.length || 0;
    const rejected = applications?.filter(app => app?.application_status === 'rejected')?.length || 0;

    // Calculate today's applications
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const todayApplications = applications?.filter(app => new Date(app?.created_at) >= today)?.length || 0;

    return {
      total,
      pending,
      processing,
      approved,
      rejected,
      todayApplications,
      approvalRate: total > 0 ? ((approved / total) * 100)?.toFixed(1) : 0
    };
  } catch (error) {
    console.error('Error fetching application status metrics:', error);
    return null;
  }
};

/**
 * Fetch error frequency trends over time
 * @param {number} hoursBack - Number of hours to look back
 * @returns {Array} Error trend data
 */
export const getErrorFrequencyTrends = async (hoursBack = 24) => {
  try {
    const { data, error } = await supabase
      ?.rpc('get_error_rate_trend', { hours_back: hoursBack });

    if (error) {
      await logSupabaseError({
        error,
        query: 'getErrorFrequencyTrends',
        table: 'error_logs',
        operation: 'RPC',
        componentName: 'monitoringService',
        functionName: 'getErrorFrequencyTrends'
      });
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching error frequency trends:', error);
    return [];
  }
};

/**
 * Fetch database performance metrics
 * @param {number} hoursBack - Number of hours to look back
 * @returns {Object} Database performance summary
 */
export const getDatabasePerformanceMetrics = async (hoursBack = 24) => {
  try {
    const { data, error } = await supabase
      ?.rpc('get_database_performance_summary', { hours_back: hoursBack });

    if (error) {
      await logSupabaseError({
        error,
        query: 'getDatabasePerformanceMetrics',
        table: 'database_performance_logs',
        operation: 'RPC',
        componentName: 'monitoringService',
        functionName: 'getDatabasePerformanceMetrics'
      });
      throw error;
    }

    return data?.[0] || null;
  } catch (error) {
    console.error('Error fetching database performance metrics:', error);
    return null;
  }
};

/**
 * Fetch email delivery health metrics
 * @param {number} hoursBack - Number of hours to look back
 * @returns {Object} Email delivery health summary
 */
export const getEmailDeliveryHealth = async (hoursBack = 24) => {
  try {
    const { data, error } = await supabase
      ?.rpc('get_email_delivery_health', { hours_back: hoursBack });

    if (error) {
      await logSupabaseError({
        error,
        query: 'getEmailDeliveryHealth',
        table: 'email_delivery_logs',
        operation: 'RPC',
        componentName: 'monitoringService',
        functionName: 'getEmailDeliveryHealth'
      });
      throw error;
    }

    return data?.[0] || null;
  } catch (error) {
    console.error('Error fetching email delivery health:', error);
    return null;
  }
};

/**
 * Fetch email delivery logs with filters
 * @param {Object} filters - Filter options
 * @returns {Array} Email delivery logs
 */
export const getEmailDeliveryLogs = async (filters = {}) => {
  try {
    let query = supabase
      ?.from('email_delivery_logs')
      ?.select('*')
      ?.order('created_at', { ascending: false })
      ?.limit(100);

    if (filters?.status) {
      query = query?.eq('delivery_status', filters?.status);
    }
    if (filters?.emailType) {
      query = query?.eq('email_type', filters?.emailType);
    }
    if (filters?.dateFrom) {
      query = query?.gte('created_at', filters?.dateFrom);
    }
    if (filters?.dateTo) {
      query = query?.lte('created_at', filters?.dateTo);
    }

    const { data, error } = await query;

    if (error) {
      await logSupabaseError({
        error,
        query: 'getEmailDeliveryLogs',
        table: 'email_delivery_logs',
        operation: 'SELECT',
        componentName: 'monitoringService',
        functionName: 'getEmailDeliveryLogs'
      });
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching email delivery logs:', error);
    return [];
  }
};

/**
 * Log email delivery event
 * @param {Object} emailData - Email delivery data
 * @returns {Object} Result with data or error
 */
export const logEmailDelivery = async (emailData) => {
  try {
    const {
      emailType,
      recipientEmail,
      recipientName,
      subject,
      deliveryStatus = 'queued',
      deliveryProvider = 'gmail_smtp',
      errorMessage = null,
      errorCode = null,
      applicationId = null,
      dealershipId = null,
      metadata = null
    } = emailData;

    const { data, error } = await supabase
      ?.from('email_delivery_logs')
      ?.insert({
        email_type: emailType,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        subject: subject,
        delivery_status: deliveryStatus,
        delivery_provider: deliveryProvider,
        error_message: errorMessage,
        error_code: errorCode,
        application_id: applicationId,
        dealership_id: dealershipId,
        sent_at: deliveryStatus === 'sent' || deliveryStatus === 'delivered' ? new Date()?.toISOString() : null,
        delivered_at: deliveryStatus === 'delivered' ? new Date()?.toISOString() : null,
        failed_at: deliveryStatus === 'failed' || deliveryStatus === 'bounced' ? new Date()?.toISOString() : null,
        metadata: metadata
      })
      ?.select()
      ?.single();

    if (error) {
      await logSupabaseError({
        error,
        query: 'logEmailDelivery',
        table: 'email_delivery_logs',
        operation: 'INSERT',
        componentName: 'monitoringService',
        functionName: 'logEmailDelivery'
      });
      throw error;
    }

    return { data };
  } catch (error) {
    console.error('Error logging email delivery:', error);
    return { error };
  }
};

/**
 * Log database performance metric
 * @param {Object} performanceData - Performance data
 * @returns {Object} Result with data or error
 */
export const logDatabasePerformance = async (performanceData) => {
  try {
    const {
      queryType,
      tableName,
      executionTimeMs,
      rowsAffected = null,
      queryHash = null,
      isSlowQuery = false,
      metadata = null
    } = performanceData;

    const { data, error } = await supabase
      ?.from('database_performance_logs')
      ?.insert({
        query_type: queryType,
        table_name: tableName,
        execution_time_ms: executionTimeMs,
        rows_affected: rowsAffected,
        query_hash: queryHash,
        is_slow_query: isSlowQuery,
        metadata: metadata
      })
      ?.select()
      ?.single();

    if (error) {
      await logSupabaseError({
        error,
        query: 'logDatabasePerformance',
        table: 'database_performance_logs',
        operation: 'INSERT',
        componentName: 'monitoringService',
        functionName: 'logDatabasePerformance'
      });
      throw error;
    }

    return { data };
  } catch (error) {
    console.error('Error logging database performance:', error);
    return { error };
  }
};

/**
 * Record system health metric
 * @param {Object} metricData - Metric data
 * @returns {Object} Result with data or error
 */
export const recordSystemHealthMetric = async (metricData) => {
  try {
    const {
      metricType,
      metricName,
      metricValue,
      metricUnit = null,
      status = 'healthy',
      thresholdWarning = null,
      thresholdCritical = null,
      metadata = null
    } = metricData;

    const { data, error } = await supabase
      ?.from('system_health_metrics')
      ?.insert({
        metric_type: metricType,
        metric_name: metricName,
        metric_value: metricValue,
        metric_unit: metricUnit,
        status: status,
        threshold_warning: thresholdWarning,
        threshold_critical: thresholdCritical,
        metadata: metadata
      })
      ?.select()
      ?.single();

    if (error) {
      await logSupabaseError({
        error,
        query: 'recordSystemHealthMetric',
        table: 'system_health_metrics',
        operation: 'INSERT',
        componentName: 'monitoringService',
        functionName: 'recordSystemHealthMetric'
      });
      throw error;
    }

    return { data };
  } catch (error) {
    console.error('Error recording system health metric:', error);
    return { error };
  }
};

/**
 * Detect and create alerts for critical failures
 * @returns {Object} Result with data or error
 */
export const detectCriticalFailures = async () => {
  try {
    const { data, error } = await supabase
      ?.rpc('detect_critical_failures');

    if (error) {
      await logSupabaseError({
        error,
        query: 'detectCriticalFailures',
        table: 'security_alerts',
        operation: 'RPC',
        componentName: 'monitoringService',
        functionName: 'detectCriticalFailures'
      });
      throw error;
    }

    return { data };
  } catch (error) {
    console.error('Error detecting critical failures:', error);
    return { error };
  }
};

/**
 * Get system health overview
 * @returns {Object} System health overview
 */
export const getSystemHealthOverview = async () => {
  try {
    // Fetch all metrics in parallel
    const [appMetrics, errorTrends, dbPerformance, emailHealth] = await Promise.all([
      getApplicationStatusMetrics(),
      getErrorFrequencyTrends(24),
      getDatabasePerformanceMetrics(24),
      getEmailDeliveryHealth(24)
    ]);

    // Calculate overall system health status
    let overallStatus = 'healthy';
    const issues = [];

    // Check error rate
    const recentErrors = errorTrends?.slice(0, 3) || [];
    const avgRecentErrors = recentErrors?.reduce((sum, item) => sum + (parseInt(item?.error_count) || 0), 0) / (recentErrors?.length || 1);
    if (avgRecentErrors > 10) {
      overallStatus = 'warning';
      issues?.push('High error rate detected');
    }
    if (avgRecentErrors > 20) {
      overallStatus = 'critical';
      issues?.push('Critical error rate');
    }

    // Check database performance
    if (dbPerformance?.avg_query_time_ms > 100) {
      overallStatus = overallStatus === 'critical' ? 'critical' : 'warning';
      issues?.push('Slow database queries');
    }

    // Check email delivery
    if (emailHealth?.failure_rate > 10) {
      overallStatus = overallStatus === 'critical' ? 'critical' : 'warning';
      issues?.push('Email delivery issues');
    }
    if (emailHealth?.failure_rate > 20) {
      overallStatus = 'critical';
      issues?.push('Critical email delivery failures');
    }

    return {
      overallStatus,
      issues,
      applicationMetrics: appMetrics,
      errorTrends,
      databasePerformance: dbPerformance,
      emailHealth
    };
  } catch (error) {
    console.error('Error getting system health overview:', error);
    return null;
  }
};
